import { useState, useMemo } from 'react';
import { useFormik } from 'formik';
import { useToast } from '@withjoy/joykit';
import { useGetContactsByIdsGuestListQuery, useUpdateContactGuestListMutation, GetEventContactsDocument } from '@graphql/generated';
import { useTranslation, useHistory } from '@shared/core';
import { yupSchema } from '@apps/guestList/utils';
import { Contacts } from '@apps/guestList/GuestList.types';

export const useUpdateController = (eventId: string, eventHandle: string, firebaseEventId: string, contactId: string) => {
  const history = useHistory();
  const [e164PhoneNumber, setE164PhoneNumber] = useState<{ [key: number]: string }>({ 0: '' });
  const { t } = useTranslation('guestList');
  const tAdmin = t('admin');
  const { toast } = useToast();

  const { data: contactData, loading: isContactDataLoading, error: contactDataError } = useGetContactsByIdsGuestListQuery({
    variables: {
      ids: [contactId]
    },
    batchMode: 'fast'
  });

  const [updateContactMutation, { loading: isUpdateContactMutationLoading }] = useUpdateContactGuestListMutation({
    // we need to refetch because getEventContacts query returns a JSON and this update won't update that response
    refetchQueries: [
      {
        query: GetEventContactsDocument,
        variables: {
          firebaseEventId,
          eventId,
          forceGuestListSynch: true
        }
      }
    ],
    awaitRefetchQueries: true
  });

  const initialContactData = contactData?.getContactsByIds?.[0];

  const initialValues = useMemo(
    () => ({
      contacts: [
        {
          firstName: initialContactData?.firstName ?? '',
          lastName: initialContactData?.lastName ?? '',
          phoneNumber: initialContactData?.phoneNumber?.[0]?.number ?? '',
          email: initialContactData?.email ?? '',
          address1: initialContactData?.household?.address?.address1 ?? '',
          address2: initialContactData?.household?.address?.address2 ?? '',
          postalCode: initialContactData?.household?.address?.postalCode ?? '',
          city: initialContactData?.household?.address?.city ?? '',
          region: initialContactData?.household?.address?.region ?? '',
          country: initialContactData?.household?.address?.country ?? '',
          countryCode: initialContactData?.household?.address?.countryCode ?? '',
          validated: initialContactData?.household?.address?.validated ?? false
        }
      ]
    }),
    [initialContactData]
  );

  const formik = useFormik<Contacts>({
    enableReinitialize: true,
    initialValues,
    validationSchema: yupSchema(tAdmin.required(), tAdmin.invalidEmail()),
    validateOnMount: true,
    onSubmit: async (values, formikHelpers) => {
      try {
        const contact = values.contacts[0];

        const mutationData = {
          address:
            contact.address1 || initialValues.contacts[0].address1
              ? {
                  address1: contact.address1 ?? '',
                  address2: contact.address2,
                  city: contact.city ?? '',
                  country: contact.country ?? '',
                  countryCode: contact.countryCode ?? '',
                  postalCode: contact.postalCode ?? '',
                  region: contact.region ?? '',
                  validated: !!contact.validated
                }
              : undefined,
          contact: {
            firebaseEventId,
            eventId,
            email: (contact.email ?? '').trim(),
            firstName: contact.firstName.trim(),
            lastName: contact.lastName.trim()
          },
          phoneNumbers:
            contact.phoneNumber || initialValues.contacts[0].phoneNumber
              ? [
                  {
                    allowSMS: true,
                    mobile: true,
                    number: (contact.phoneNumber ?? '').trim()
                  }
                ]
              : undefined
        };
        await updateContactMutation({ variables: { id: contactId, data: mutationData } });
        setE164PhoneNumber({ 0: '' });
        formikHelpers.resetForm();
        history.push(`/${eventHandle}/edit/guestlist`);
        toast(`${values.contacts.length > 1 ? tAdmin.guests() : tAdmin.guest()} ${tAdmin.updated()} ${tAdmin.successfully()}.`, { position: 'bottom-center' });
      } catch {
        //add telemetry
        toast(`${tAdmin.error()} ${tAdmin.update()}.`, { position: 'bottom-center' });
      }
    }
  });

  return {
    tAdmin,
    showErrorPage: !!contactDataError,
    isLoading: isContactDataLoading,
    formik,
    isMutationLoading: isUpdateContactMutationLoading,
    e164PhoneNumber,
    setE164PhoneNumber
  };
};
